import type { ReactNode } from "react";
import { Logo } from "@sunfish/ui";

interface AuthLayoutProps {
  children: ReactNode;
}

const RIPPLE_RADII = [90, 180, 280, 390, 510, 640];

function BrandPanel() {
  return (
    <div className="relative flex flex-col gap-6 overflow-hidden bg-blue-500 px-6 py-8 sm:px-10 lg:justify-between lg:gap-0 lg:p-12 xl:p-16">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-400/25 via-blue-500 to-blue-700"
        aria-hidden
      />
      <svg
        className="auth-ripple-breathe pointer-events-none absolute -bottom-40 -left-32 h-[140%] w-[140%] text-sidebar-foreground"
        viewBox="0 0 800 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        {RIPPLE_RADII.map((r) => (
          <circle
            key={r}
            cx="180"
            cy="640"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeWidth={1.5}
          />
        ))}
      </svg>
      <div
        className="pointer-events-none absolute -left-24 bottom-24 h-96 w-96 rounded-full bg-brand/[0.06] blur-3xl"
        aria-hidden
      />

      <div className="relative flex items-center gap-3">
        <Logo className="h-8 w-8 lg:h-9 lg:w-9" />
        <span className="font-display text-xl font-extrabold tracking-tight text-sidebar-foreground">
          Sunfish Events
        </span>
      </div>

      <div className="relative max-w-md">
        <p className="text-eyebrow uppercase tracking-[0.18em] text-brand/90">
          Event registration software
        </p>
        <h1 className="font-display mt-3 text-2xl font-extrabold leading-[1.12] tracking-tight text-sidebar-foreground lg:mt-4 lg:text-[2.5rem] lg:leading-[1.08]">
          Powerful registration, without the complexity.
        </h1>
        <p className="mt-4 max-w-sm text-body leading-relaxed text-sidebar-foreground/70 lg:mt-5">
          Create branded pages, collect payments, and manage every attendee, all
          in one place.
        </p>
      </div>
    </div>
  );
}

export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen w-full flex-col bg-background lg:grid lg:grid-cols-[1fr_1.1fr]">
      <BrandPanel />
      <div className="flex flex-1 flex-col items-center justify-start px-6 py-10 sm:px-10 lg:justify-center lg:py-12">
        <div className="w-full max-w-[400px]">{children}</div>
      </div>
    </div>
  );
}
